import { useState, useCallback, useRef, useEffect } from 'react';
import api from './tablaServidorApi';
import { descargar, mensajeError } from './descargas';

const PAGE_SIZE = 100;

/**
 * Datos de una tabla estándar en servidor, con scroll infinito por tandas de 100.
 * `consulta` = { sortBy, sortDir, colFilters, columnas, q } del estado de la tabla y `params` los
 * filtros propios de la pantalla: al cambiar cualquiera se pide otra vez la primera página.
 * `habilitado` espera a que estén cargadas las preferencias del usuario; cambiar `refresco` recarga.
 */
const useTablaServidor = ({
  ruta,
  archivo,
  consulta,
  params = {},
  habilitado = true,
  refresco = 0,
}) => {
  const [rows, setRows] = useState(null);
  const [total, setTotal] = useState(0);
  const [isFetching, setIsFetching] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [exportError, setExportError] = useState(null);
  const [fallo, setFallo] = useState(false);
  const [reintento, setReintento] = useState(0);
  const offsetRef = useRef(0);
  const peticionRef = useRef(0);
  const pedidoRef = useRef({ consulta, params });
  pedidoRef.current = { consulta, params };

  const pedir = (offset) => {
    const { consulta: c, params: p } = pedidoRef.current;
    return api.tabla(ruta, { ...p, ...c, limit: PAGE_SIZE, offset });
  };

  const clave = JSON.stringify({ consulta, params, refresco, reintento });
  useEffect(() => {
    if (!habilitado) return;
    const peticion = peticionRef.current + 1;
    peticionRef.current = peticion;
    setIsFetching(true);
    setFallo(false);
    offsetRef.current = 0;
    pedir(0)
      .then((data) => {
        if (peticion !== peticionRef.current) return; // llegó una búsqueda más reciente
        setRows(data.rows);
        setTotal(data.total);
        offsetRef.current = data.rows.length;
      })
      .catch(() => {
        if (peticion !== peticionRef.current) return;
        setRows([]);
        setTotal(0);
        setFallo(true);
      })
      .finally(() => {
        if (peticion === peticionRef.current) setIsFetching(false);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clave, habilitado, ruta]);

  const hasMore = rows !== null && rows.length < total;

  const loadMore = useCallback(async () => {
    // Mientras se recarga la primera página (otro orden/filtro) no se pide más: el offset es del pedido anterior.
    if (isLoadingMore || isFetching || !hasMore) return;
    const peticion = peticionRef.current;
    setIsLoadingMore(true);
    try {
      const data = await pedir(offsetRef.current);
      if (peticion !== peticionRef.current) return;
      if (data.rows.length > 0) {
        setRows((prev) => [...(prev || []), ...data.rows]);
        offsetRef.current += data.rows.length;
      }
      setTotal(data.total);
    } catch (_err) {
      // Se conserva lo ya cargado; el centinela volverá a pedir al hacer scroll.
    } finally {
      setIsLoadingMore(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoadingMore, isFetching, hasMore, ruta]);

  // Valores reales de una columna bajo los filtros actuales (sin el de la propia columna).
  const loadFacets = useCallback(
    async (columna) => {
      const { consulta: c, params: p } = pedidoRef.current;
      const data = await api.facetas(ruta, { ...p, colFilters: c.colFilters, q: c.q, columna });
      return data?.valores || [];
    },
    [ruta],
  );

  const exportar = useCallback(
    async (columnas) => {
      setIsExporting(true);
      setExportError(null);
      try {
        const { consulta: c, params: p } = pedidoRef.current;
        const blob = await api.exportar(ruta, { ...p, ...c, columnas });
        descargar(blob, `${archivo || ruta}-${new Date().toISOString().slice(0, 10)}.xlsx`);
      } catch (err) {
        setExportError(await mensajeError(err));
      } finally {
        setIsExporting(false);
      }
    },
    [ruta, archivo],
  );

  const reintentar = useCallback(() => setReintento((n) => n + 1), []);

  return {
    rows,
    total,
    hasMore,
    isFetching,
    fallo,
    reintentar,
    isLoadingMore,
    isExporting,
    exportError,
    loadMore,
    loadFacets,
    exportar,
  };
};

export default useTablaServidor;
